import { useEffect } from "react";

/**
 * FinanceLocal — SEO helper
 *
 * Vanilla-DOM per-page meta updater. No react-helmet dep. Cleans up on unmount
 * so route transitions leave a well-formed <head>.
 *
 * Usage:
 *   useSEO({
 *     title: "Available Notes — FinanceLocal",
 *     description: "Browse 79 cash-flowing vehicle notes...",
 *     canonical: "https://myfinancelocal.com/notes",
 *     ogImage: "https://myfinancelocal.com/og-image.svg",
 *     jsonLd: [{ "@context": "https://schema.org", ... }],
 *   });
 */

const SITE_ORIGIN = "https://myfinancelocal.com";
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-image.svg`;

// Any tag we programmatically add gets this flag so we can safely remove it on
// unmount without touching hand-authored <head> tags from index.html.
const OWNED_ATTR = "data-seo-managed";

function _upsertMeta({ name, property, content }) {
  if (!content) return null;
  const sel = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("meta");
    if (name) el.setAttribute("name", name);
    if (property) el.setAttribute("property", property);
    el.setAttribute(OWNED_ATTR, "");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}

function _upsertLink({ rel, href }) {
  if (!href) return null;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute(OWNED_ATTR, "");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

function _injectJsonLd(id, obj) {
  if (!obj) return null;
  // Reuse an existing script tag with the same id, else create one.
  const scriptId = `ld-${id}`;
  let el = document.getElementById(scriptId);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("id", scriptId);
    el.setAttribute(OWNED_ATTR, "");
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
  return el;
}

export function useSEO(config) {
  const {
    title,
    description,
    canonical,
    ogImage = DEFAULT_OG_IMAGE,
    ogType = "website",
    keywords,
    noIndex = false,
    jsonLd,
  } = config || {};

  useEffect(() => {
    const previousTitle = document.title;

    if (title) document.title = title;

    // Robots
    _upsertMeta({ name: "robots", content: noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large" });

    if (description) {
      _upsertMeta({ name: "description", content: description });
      _upsertMeta({ property: "og:description", content: description });
      _upsertMeta({ name: "twitter:description", content: description });
    }
    if (title) {
      _upsertMeta({ property: "og:title", content: title });
      _upsertMeta({ name: "twitter:title", content: title });
    }
    if (keywords) {
      _upsertMeta({ name: "keywords", content: keywords });
    }

    // Canonical + og:url
    const canonicalUrl = canonical || (typeof window !== "undefined" ? window.location.origin + window.location.pathname : "");
    if (canonicalUrl) {
      _upsertLink({ rel: "canonical", href: canonicalUrl });
      _upsertMeta({ property: "og:url", content: canonicalUrl });
    }

    _upsertMeta({ property: "og:type", content: ogType });
    _upsertMeta({ property: "og:site_name", content: "FinanceLocal" });
    if (ogImage) {
      _upsertMeta({ property: "og:image", content: ogImage });
      _upsertMeta({ name: "twitter:image", content: ogImage });
    }
    _upsertMeta({ name: "twitter:card", content: "summary_large_image" });

    // JSON-LD (may be array). Give each blob a stable id derived from its @type.
    const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []);
    const injectedIds = [];
    jsonLdArray.forEach((blob, idx) => {
      if (!blob) return;
      const id = `page-${(blob["@type"] || "block").toString().toLowerCase()}-${idx}`;
      _injectJsonLd(id, blob);
      injectedIds.push(`ld-${id}`);
    });

    return () => {
      document.title = previousTitle;
      // Only remove per-page JSON-LD blobs — global blobs from index.html stay.
      injectedIds.forEach((elId) => {
        const el = document.getElementById(elId);
        if (el && el.hasAttribute(OWNED_ATTR)) el.remove();
      });
    };
  }, [title, description, canonical, ogImage, ogType, keywords, noIndex, JSON.stringify(jsonLd)]);
}

// Small helpers exported for pages that want to construct JSON-LD inline.
export const jsonLdBreadcrumb = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: it.url,
  })),
});

export const jsonLdFAQ = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});
